<template>
  <div class="profile-page">
    <van-nav-bar fixed title="个人资料" left-text="返回" left-arrow @click-left="goBack" />

    <!-- 头像 -->
    <div class="avatar-card">
      <div class="avatar-wrap" @click="chooseAvatar">
        <img :src="avatarUrl" alt="头像" @error="onAvatarError" />
        <div class="avatar-mask">
          <van-icon name="photograph" />
        </div>
      </div>
      <p class="avatar-tip">{{ uploading ? '头像上传中...' : '点击更换头像' }}</p>
      <input
        ref="fileInput"
        type="file"
        accept="image/*"
        style="display: none;"
        @change="onFileChange"
      >
    </div>

    <!-- 昵称 / 手机号 -->
    <van-cell-group inset class="form-group">
      <van-field
        v-model="nickname"
        label="昵称"
        placeholder="请输入昵称（1~20 个字符）"
        maxlength="20"
        clearable
      />
      <van-field
        v-model="mobile"
        label="手机号"
        placeholder="请输入 11 位手机号"
        type="tel"
        maxlength="11"
      />
      <van-field
        v-if="mobileChanged"
        v-model="smsCode"
        label="验证码"
        placeholder="请输入短信验证码"
        maxlength="6"
        center
      >
        <template #button>
          <van-button
            size="small"
            type="primary"
            :disabled="!canSendCode || sendingCode || countdown > 0"
            @click="sendCode"
          >
            {{ countdown > 0 ? countdown + 's' : '获取验证码' }}
          </van-button>
        </template>
      </van-field>
    </van-cell-group>
    <p class="field-tip" v-if="mobileChanged">更换手机号需要短信验证码验证，验证码 5 分钟内有效。</p>
    <p class="field-tip" v-else-if="original.mobile">当前绑定手机号：{{ original.mobile }}</p>
    <p class="field-tip" v-else>尚未绑定手机号，绑定后可用手机号验证码登录。</p>

    <!-- 登录密码：查看商家资质等安全验证需要用到；微信/邮箱注册的账号可在此设置密码 -->
    <van-cell-group inset class="form-group password-group" ref="passwordGroup">
      <van-field
        v-if="hasPassword"
        v-model="oldPassword"
        type="password"
        label="当前密码"
        placeholder="请输入当前登录密码"
        maxlength="20"
        clearable
      />
      <van-field
        v-model="newPassword"
        type="password"
        :label="hasPassword ? '新密码' : '登录密码'"
        :placeholder="hasPassword ? '请输入新密码（6~20 位）' : '请设置登录密码（6~20 位）'"
        maxlength="20"
        clearable
      />
      <van-field
        v-model="confirmPassword"
        type="password"
        label="确认密码"
        placeholder="请再次输入密码"
        maxlength="20"
        clearable
      />
    </van-cell-group>
    <p class="field-tip">
      {{ hasPassword
        ? '登录密码：填写「当前密码 + 新密码」后点击页面底部「保存」即可修改（忘记密码可退出登录，在登录页点「忘记密码？」用手机验证码重置）。'
        : '登录密码：设置后可使用「账号或手机号 + 密码」登录，并用于查看商家资质等安全验证；填写密码后点击页面底部「保存」生效。' }}
    </p>

    <div class="save-bar">
      <van-button
        type="primary"
        block
        round
        :loading="saving"
        loading-text="保存中..."
        @click="save"
      >保存</van-button>
    </div>
  </div>
</template>


<script>
import { Toast } from 'vant'
import {
  getUserInfoDetail,
  updateProfileApi,
  sendBindCodeApi,
  bindMobileApi,
  uploadAvatarApi,
  setPasswordApi
} from '@/api/user'

// 本地兜底头像（用户未设置头像 / 头像加载失败时使用）
const DEFAULT_AVATAR = require('@/assets/default-avatar.svg')

export default {
  name: 'Profile',
  data () {
    return {
      // 已保存的资料（用于比对哪些字段被修改）
      original: { nickname: '', avatar: '', mobile: '' },
      nickname: '',
      avatar: '',
      mobile: '',
      smsCode: '',
      uploading: false,
      saving: false,
      sendingCode: false,
      countdown: 0,
      countdownTimer: null,
      defaultAvatar: DEFAULT_AVATAR,
      // ===== 登录密码 =====
      // 是否已设置过登录密码（未设置时只需填新密码，无需「当前密码」）
      hasPassword: false,
      oldPassword: '',
      newPassword: '',
      confirmPassword: ''
    }
  },
  computed: {
    isLogin () {
      return !!this.$store.getters.token
    },
    avatarUrl () {
      return this.avatar || this.original.avatar || this.defaultAvatar
    },
    nicknameChanged () {
      return this.nickname.trim() !== (this.original.nickname || '')
    },
    avatarChanged () {
      return (this.avatar || '') !== (this.original.avatar || '')
    },
    mobileChanged () {
      return this.mobile.trim() !== (this.original.mobile || '')
    },
    canSendCode () {
      return /^1[3-9]\d{9}$/.test(this.mobile.trim())
    },
    // 是否填写了密码相关字段（点「保存」时决定要不要一并保存密码）
    passwordTouched () {
      return !!(this.oldPassword || this.newPassword || this.confirmPassword)
    }
  },
  created () {
    if (!this.isLogin) {
      this.$router.replace('/login?redirect=/profile')
      return
    }
    this.loadUserInfo()
  },
  mounted () {
    // 从「查看商家资质」等处跳转而来（?section=password）：滚动到登录密码区域
    if (this.$route.query.section === 'password') {
      this.$nextTick(() => {
        const el = this.$refs.passwordGroup && this.$refs.passwordGroup.$el
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    }
  },
  beforeDestroy () {
    if (this.countdownTimer) clearInterval(this.countdownTimer)
  },
  methods: {
    goBack () {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.replace('/user')
      }
    },
    async loadUserInfo () {
      try {
        const { data } = await getUserInfoDetail()
        this.original = {
          nickname: data.nickname || '',
          avatar: data.avatar || '',
          mobile: data.mobile || ''
        }
        this.nickname = this.original.nickname
        this.avatar = this.original.avatar
        this.mobile = this.original.mobile
        // 是否已设置登录密码（未设置时展示「设置登录密码」）
        this.hasPassword = Number(data.has_password) === 1
        // 同步到 vuex（并写入本地缓存）
        this.$store.commit('user/setUserInfo', { ...this.$store.state.user.userInfo, ...data })
      } catch (err) {
        Toast(typeof err === 'string' ? err : '获取用户信息失败')
      }
    },
    chooseAvatar () {
      if (this.uploading) return
      this.$refs.fileInput.click()
    },
    // 选择图片 -> 上传 -> 记录头像地址（点击「保存」时统一提交）
    async onFileChange (e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!file) return
      if (!/^image\//.test(file.type)) {
        Toast('请选择图片文件')
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        Toast('头像图片不能超过 5MB')
        return
      }
      this.uploading = true
      try {
        const res = await uploadAvatarApi(file)
        this.avatar = (res.data && res.data.url) || ''
        Toast('头像已上传，点击「保存」生效')
      } catch (err) {
        Toast(typeof err === 'string' ? err : '头像上传失败，请重试')
      } finally {
        this.uploading = false
      }
    },
    onAvatarError () {
      if (this.avatar) this.avatar = ''
      if (this.original.avatar) this.original.avatar = ''
    },
    // 获取绑定手机号验证码
    async sendCode () {
      const mobile = this.mobile.trim()
      if (!/^1[3-9]\d{9}$/.test(mobile)) {
        Toast('请输入正确的手机号')
        return
      }
      if (!this.mobileChanged) {
        Toast('手机号未发生变化，无需验证')
        return
      }
      this.sendingCode = true
      try {
        const res = await sendBindCodeApi(mobile)
        Toast(res.message || '验证码已发送')
        this.startCountdown()
      } catch (err) {
        Toast(typeof err === 'string' ? err : '验证码发送失败')
      } finally {
        this.sendingCode = false
      }
    },
    startCountdown () {
      this.countdown = 60
      if (this.countdownTimer) clearInterval(this.countdownTimer)
      this.countdownTimer = setInterval(() => {
        this.countdown--
        if (this.countdown <= 0) {
          clearInterval(this.countdownTimer)
          this.countdownTimer = null
        }
      }, 1000)
    },
    // 保存：按修改的字段分别提交（头像 / 昵称走资料接口，手机号走绑定接口，登录密码走设置密码接口）
    async save () {
      if (!this.nicknameChanged && !this.avatarChanged && !this.mobileChanged && !this.passwordTouched) {
        Toast('资料没有变化')
        return
      }
      if (this.nicknameChanged) {
        const len = this.nickname.trim().length
        if (len < 1 || len > 20) {
          Toast('昵称需要 1~20 个字符')
          return
        }
      }
      if (this.mobileChanged) {
        if (!this.canSendCode) {
          Toast('请输入正确的手机号')
          return
        }
        if (!this.smsCode.trim()) {
          Toast('请输入短信验证码')
          return
        }
      }
      // 登录密码：填写了任意密码字段时，随「保存」一并提交
      if (this.passwordTouched) {
        if (this.hasPassword && !this.oldPassword) {
          Toast('请输入当前登录密码')
          return
        }
        if (!this.newPassword) {
          Toast('请输入新密码')
          return
        }
        if (this.newPassword.length < 6 || this.newPassword.length > 20) {
          Toast('密码长度需为 6~20 位')
          return
        }
        if (this.newPassword !== this.confirmPassword) {
          Toast('两次输入的密码不一致')
          return
        }
      }

      this.saving = true
      try {
        // 1. 昵称 / 头像
        if (this.nicknameChanged || this.avatarChanged) {
          const payload = {}
          if (this.nicknameChanged) payload.nickname = this.nickname.trim()
          if (this.avatarChanged) payload.avatar = this.avatar
          const res = await updateProfileApi(payload)
          if (res.data) {
            this.original.nickname = res.data.nickname || this.original.nickname
            this.original.avatar = res.data.avatar || ''
            this.nickname = this.original.nickname
            this.avatar = this.original.avatar
            this.$store.commit('user/setUserInfo', { ...this.$store.state.user.userInfo, ...res.data })
          }
        }
        // 2. 手机号
        if (this.mobileChanged) {
          const res = await bindMobileApi(this.mobile.trim(), this.smsCode.trim())
          this.original.mobile = (res.data && res.data.mobile) || this.mobile.trim()
          this.mobile = this.original.mobile
          this.smsCode = ''
          this.$store.commit('user/setUserInfo', {
            ...this.$store.state.user.userInfo,
            mobile: this.original.mobile
          })
        }
        // 3. 登录密码：与资料一起保存（已设置过密码时需先填「当前密码」）
        const savePassword = this.passwordTouched
        if (savePassword) {
          await setPasswordApi(this.oldPassword, this.newPassword)
          this.hasPassword = true
          this.oldPassword = ''
          this.newPassword = ''
          this.confirmPassword = ''
        }
        Toast.success('保存成功')
        this.$router.replace('/user')
      } catch (err) {
        Toast(typeof err === 'string' ? err : '保存失败，请重试')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style lang="less" scoped>
.profile-page {
  min-height: 100vh;
  background-color: #f7f7f7;
  padding-top: 46px;

  /deep/ .van-nav-bar__arrow { color: #333; }
  /deep/ .van-nav-bar__text { color: #333; }
  /deep/ .van-nav-bar { z-index: 100; }

  .avatar-card {
    background: #fff;
    padding: 22px 0 16px;
    display: flex;
    flex-direction: column;
    align-items: center;

    .avatar-wrap {
      position: relative;
      width: 90px;
      height: 90px;
      border-radius: 50%;
      overflow: hidden;
      background: #f2f2f2;
      cursor: pointer;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }

      .avatar-mask {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 26px;
        background: rgba(0, 0, 0, 0.45);
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 15px;
      }
    }

    .avatar-tip {
      margin-top: 10px;
      font-size: 13px;
      color: #999;
    }
  }

  .form-group {
    margin-top: 12px;
  }

  .password-group {
    margin-top: 18px;
  }

  .inline-action {
    display: flex;
    justify-content: flex-end;
    padding: 12px 20px 0;

    /deep/ .van-button--primary {
      background: linear-gradient(90deg, #ff6b35, #ff8c42);
      border: none;
    }
  }

  .field-tip {
    margin: 10px 20px 0;
    font-size: 12px;
    color: #999;
    line-height: 1.6;
  }

  .save-bar {
    padding: 24px 16px 30px;

    /deep/ .van-button--primary {
      background: linear-gradient(90deg, #ff6b35, #ff8c42);
      border: none;
    }
  }
}
</style>

